import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './cn';

/**
 * Labelled input or textarea (design-spec §3 "Inputs").
 *
 * - Label above: uppercase 12.5/700, ls 1.2, 7px gap. `labelStyle="sub"` gives
 *   the sentence-case 12/600 sub-label ("Street & number").
 * - Optional `hint` inline after the label ("(optional)", "(for salon updates)").
 * - Control: 52px (`size="lg"` 54 for auth), radius 14, surface, 1px
 *   line-strong, padding 0 18, 15px text. Textarea 84px, padding 14×18.
 * - Focus: accent border + 3px accent/15 ring. Error: accent border and a
 *   12.5px link-coloured message below (spec's suggested states).
 *
 * a11y: `<label htmlFor>`, `aria-invalid` when `error` is set, and
 * `aria-describedby` pointing at the helper and/or error text.
 */
interface FieldBaseProps {
  label: ReactNode;
  hint?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  labelStyle?: 'caps' | 'sub';
  size?: 'md' | 'lg';
  /** Hide the label visually (still announced). */
  hideLabel?: boolean;
  /** Classes for the outer wrapper. */
  className?: string;
  /** Classes for the control itself. */
  controlClassName?: string;
}

type InputFieldProps = FieldBaseProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'className'> & {
    multiline?: false;
  };
type TextareaFieldProps = FieldBaseProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & {
    multiline: true;
  };

export type FieldProps = InputFieldProps | TextareaFieldProps;

export const controlClasses =
  'w-full rounded-14 border border-sl-field-line bg-sl-surface px-[18px] font-sans text-[15px] text-sl-ink ' +
  'placeholder:text-sl-label-2 transition-[border-color,box-shadow] ' +
  'focus:outline-none focus:border-sl-accent focus:ring-[3px] focus:ring-sl-accent/15 ' +
  'disabled:cursor-not-allowed disabled:bg-sl-disabled-fill disabled:text-sl-disabled-text ' +
  'aria-[invalid=true]:border-sl-accent';

export const Field = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  FieldProps
>(function Field(props, ref) {
  const {
    label,
    hint,
    helper,
    error,
    labelStyle = 'caps',
    size = 'md',
    hideLabel,
    className,
    controlClassName,
    id: idProp,
    ...controlProps
  } = props;
  const autoId = useId();
  const id = idProp ?? `field-${autoId}`;
  const helperId = helper ? `${id}-helper` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy =
    [controlProps['aria-describedby'], helperId, errorId]
      .filter(Boolean)
      .join(' ') || undefined;

  const a11y = {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
  } as const;

  let control: ReactNode;
  if (props.multiline) {
    const { multiline: _m, ...rest } =
      controlProps as TextareaHTMLAttributes<HTMLTextAreaElement> & {
        multiline: true;
      };
    control = (
      <textarea
        ref={ref as Ref<HTMLTextAreaElement>}
        {...rest}
        {...a11y}
        className={cn(
          controlClasses,
          'min-h-[84px] resize-y py-3.5 leading-relaxed',
          controlClassName
        )}
      />
    );
  } else {
    const { multiline: _m, ...rest } =
      controlProps as InputHTMLAttributes<HTMLInputElement> & {
        multiline?: false;
      };
    control = (
      <input
        ref={ref as Ref<HTMLInputElement>}
        {...rest}
        {...a11y}
        className={cn(
          controlClasses,
          size === 'lg' ? 'h-ctl-lg' : 'h-ctl-input',
          controlClassName
        )}
      />
    );
  }

  return (
    <div className={cn('flex flex-col gap-[7px]', className)}>
      <label
        htmlFor={id}
        className={cn(
          'font-sans',
          labelStyle === 'caps'
            ? 'text-[12.5px] font-bold uppercase tracking-[1.2px] text-sl-label-2'
            : 'text-[12px] font-semibold text-sl-muted',
          hideLabel && 'sr-only'
        )}
      >
        {label}
        {hint && ' '}
        {hint && (
          <span className="ml-0.5 text-[12.5px] font-medium normal-case tracking-normal text-sl-label-2">
            {hint}
          </span>
        )}
      </label>
      {control}
      {helper && (
        <p id={helperId} className="font-sans text-[12.5px] text-sl-muted">
          {helper}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="font-sans text-[12.5px] font-medium text-sl-link"
        >
          {error}
        </p>
      )}
    </div>
  );
});
